/**
 * Super Admin Dashboard
 *
 * Main dashboard for platform administration with comprehensive metrics,
 * platform health status, charts, recent activity, and quick actions.
 *
 * Features:
 * - System health panel with service status
 * - Primary metrics: Accounts, Subscriptions, Support, Revenue
 * - Charts: Accounts growth, Support queue breakdown
 * - Secondary metrics: Admin users, AI flags, Past due, Failed jobs
 * - Recent activity timeline
 * - Alerts panel for items requiring attention
 */
import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Users,
  MessageSquare,
  DollarSign,
  Shield,
  Flag,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';
import {
  MetricCard,
  MetricCardSkeleton,
  SystemHealthPanel,
  AccountsOverviewChart,
  AccountsOverviewChartSkeleton,
  SupportQueueChart,
  SupportQueueChartSkeleton,
  RecentActivityList,
  AlertsPanel,
  type Alert,
} from '../components/dashboard';
import { useAccountsStore } from '../stores/useAccountsStore';
import { useBillingStore } from '../stores/useBillingStore';
import { useSupportStore } from '../stores/useSupportStore';
import { useOperationsStore } from '../stores/useOperationsStore';
import { useAuditStore } from '../stores/useAuditStore';
import { useAdminUsersStore } from '../stores/useAdminUsersStore';

// ============================================
// Selectors
// ============================================

const useRecentLogs = (limit: number) =>
  useAuditStore((state) => (state.auditLogs ?? []).slice(0, limit));

// ============================================
// Component
// ============================================

export function Dashboard() {
  // Stores
  const accountsStore = useAccountsStore();
  const billingStore = useBillingStore();
  const supportStore = useSupportStore();
  const operationsStore = useOperationsStore();
  const auditStore = useAuditStore();
  const adminUsersStore = useAdminUsersStore();

  // Data
  const billingDashboard = billingStore.dashboard;
  const queueSummary = supportStore.queueSummary;
  const aiFlags = supportStore.aiFlags;
  const healthStatus = operationsStore.healthStatus;
  const queueStats = operationsStore.queueStats;
  const recentLogs = useRecentLogs(10);
  const adminUsers = adminUsersStore.users;
  const pastDueAccounts = billingStore.pastDueAccounts;

  // Loading states
  const isLoadingAccounts = accountsStore.isLoadingList;
  const isLoadingBilling = billingStore.isLoadingDashboard;
  const isLoadingSupport = supportStore.isLoadingQueueSummary || supportStore.isLoadingAiFlags;
  const isLoadingOperations =
    operationsStore.isLoadingHealth || operationsStore.isLoadingQueues;
  const isLoadingAudit = auditStore.isLoadingLogs;
  const isLoadingUsers = adminUsersStore.isLoadingList;

  // Load all data on mount
  useEffect(() => {
    accountsStore.loadAccounts(1);
    accountsStore.loadAccountGrowthTrend();
    billingStore.loadDashboard();
    billingStore.loadPastDueAccounts();
    supportStore.loadQueueSummary();
    supportStore.loadAiFlags();
    operationsStore.loadHealthStatus();
    operationsStore.loadQueueStats();
    auditStore.loadAuditLogs(1);
    adminUsersStore.loadUsers(1);
  }, []);

  // ============================================
  // Computed Metrics
  // ============================================

  const totalAccounts = accountsStore.pagination?.total ?? 0;
  const activeSubscriptions = billingDashboard?.metrics?.activeSubscriptions || 0;
  const openSupportTickets =
    (queueSummary?.open || 0) + (queueSummary?.inProgress || 0);
  const monthlyRevenue = billingDashboard?.metrics?.mrr || 0;
  const activeAdminUsers = adminUsers?.filter((u) => u.isActive).length ?? 0;
  const aiFlagsPending = aiFlags?.length ?? 0;
  const pastDueCount = pastDueAccounts?.length ?? 0;
  const pastDueAmount = pastDueAccounts?.reduce((sum, acc) => sum + acc.amountDue, 0) ?? 0;
  const failedJobs = queueStats?.reduce((sum, q) => sum + (q.failed || 0), 0) ?? 0;

  // ============================================
  // Alerts Aggregation
  // ============================================

  const alerts = useMemo(() => {
    const alertsList: Alert[] = [];

    // Past due accounts
    if (pastDueCount > 0) {
      alertsList.push({
        id: 'past-due',
        type: 'past-due',
        title: 'Past Due Accounts',
        description: `${pastDueCount} account${pastDueCount > 1 ? 's' : ''} with overdue payments`,
        severity: 'high',
        count: pastDueCount,
        navigateTo: '/billing',
      });
    }

    // AI flagged content
    if (aiFlagsPending > 0) {
      alertsList.push({
        id: 'ai-flags',
        type: 'ai-flags',
        title: 'AI Flagged Content',
        description: `${aiFlagsPending} message${aiFlagsPending > 1 ? 's' : ''} flagged for review`,
        severity: 'medium',
        count: aiFlagsPending,
        navigateTo: '/support',
      });
    }

    // Unhealthy services
    const unhealthyServices =
      healthStatus?.services?.filter((s) => s.status !== 'healthy') || [];
    if (unhealthyServices.length > 0) {
      alertsList.push({
        id: 'unhealthy-services',
        type: 'unhealthy-service',
        title: 'Service Health Issues',
        description: `${unhealthyServices.length} service${unhealthyServices.length > 1 ? 's' : ''} degraded or down`,
        severity: 'high',
        count: unhealthyServices.length,
        navigateTo: '/operations',
      });
    }

    // Failed queue jobs
    if (failedJobs > 0) {
      alertsList.push({
        id: 'failed-jobs',
        type: 'failed-jobs',
        title: 'Failed Queue Jobs',
        description: `${failedJobs} job${failedJobs > 1 ? 's' : ''} failed to process`,
        severity: 'medium',
        count: failedJobs,
        navigateTo: '/operations',
      });
    }

    // Sort by severity
    const severityOrder = { high: 0, medium: 1, low: 2 };
    return alertsList.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [pastDueCount, aiFlagsPending, healthStatus, failedJobs]);

  // ============================================
  // Render
  // ============================================

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          Dashboard
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Platform overview and key metrics
        </p>
      </motion.div>

      {/* System Health Status */}
      <SystemHealthPanel
        healthStatus={healthStatus}
        isLoading={isLoadingOperations}
      />

      {/* Primary Metrics Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        {isLoadingAccounts ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="Total Accounts"
            value={totalAccounts}
            icon={Building2}
            format="number"
            trend={
              accountsStore.accountGrowthRate !== null
                ? {
                    value: Math.abs(accountsStore.accountGrowthRate),
                    isPositive: accountsStore.accountGrowthRate >= 0,
                  }
                : undefined
            }
            description="Active tenant accounts"
          />
        )}

        {isLoadingBilling ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="Active Subscriptions"
            value={activeSubscriptions}
            icon={Users}
            format="number"
            trend={
              billingDashboard?.metrics?.growthRate
                ? {
                    value: billingDashboard.metrics.growthRate,
                    isPositive: billingDashboard.metrics.growthRate > 0,
                  }
                : undefined
            }
            description="Paying customers"
          />
        )}

        {isLoadingSupport ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="Open Support Tickets"
            value={openSupportTickets}
            icon={MessageSquare}
            format="number"
            description="Requiring attention"
          />
        )}

        {isLoadingBilling ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="Monthly Revenue"
            value={monthlyRevenue}
            icon={DollarSign}
            format="currency"
            trend={
              billingDashboard?.metrics?.growthRate
                ? {
                    value: billingDashboard.metrics.growthRate,
                    isPositive: billingDashboard.metrics.growthRate > 0,
                  }
                : undefined
            }
            description="Current MRR"
          />
        )}
      </motion.div>

      {/* Charts Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
        className="grid gap-6 lg:grid-cols-3"
      >
        {isLoadingAccounts || accountsStore.isLoadingGrowthTrend ? (
          <AccountsOverviewChartSkeleton className="lg:col-span-2" />
        ) : (
          <AccountsOverviewChart
            className="lg:col-span-2"
            data={accountsStore.accountGrowthTrend ?? undefined}
          />
        )}

        {isLoadingSupport ? (
          <SupportQueueChartSkeleton />
        ) : (
          <SupportQueueChart
            data={
              queueSummary
                ? {
                    open: queueSummary.open,
                    inProgress: queueSummary.inProgress,
                    resolved: queueSummary.resolved,
                    closed: queueSummary.closed,
                  }
                : undefined
            }
          />
        )}
      </motion.div>

      {/* Secondary Metrics Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        {isLoadingUsers ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="Admin Users"
            value={activeAdminUsers}
            icon={Shield}
            format="number"
            description="Active administrators"
          />
        )}

        {isLoadingSupport ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="AI Flags Pending"
            value={aiFlagsPending}
            icon={Flag}
            format="number"
            description="Awaiting review"
          />
        )}

        {isLoadingBilling ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="Past Due Accounts"
            value={pastDueCount}
            icon={AlertCircle}
            format="number"
            description={
              pastDueAmount > 0
                ? `${new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(pastDueAmount)} total`
                : 'No overdue payments'
            }
          />
        )}

        {isLoadingOperations ? (
          <MetricCardSkeleton />
        ) : (
          <MetricCard
            title="Failed Jobs"
            value={failedJobs}
            icon={AlertTriangle}
            format="number"
            description="Queue failures"
          />
        )}
      </motion.div>

      {/* Bottom Section: Activity & Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.25 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        <RecentActivityList logs={recentLogs} isLoading={isLoadingAudit} />
        <AlertsPanel
          alerts={alerts}
          isLoading={isLoadingBilling || isLoadingSupport || isLoadingOperations}
        />
      </motion.div>
    </div>
  );
}

export default Dashboard;
